import React, { useState, useRef, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { useForm } from 'react-hook-form'
import { twMerge } from 'tailwind-merge'
import { getAssetUrl } from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {
  Modal,
  Events,
  EventsEmitter,
  Field,
  Button,
  ProductQuantity,
  AlertBox
} from 'msp-components'
import {
  useCustomer,
  useBasket,
  useStandingOrders,
  useConfig,
  PAYMENT_METHODS,
  MINIMUM_TEA_PACKS_PER_ORDER,
  useCountryData,
  ICustomerAddress,
  PaymentInstrument
} from 'msp-integrations'
import { OrderSubscriptionModalProps } from './OrderSubscriptionModal.types'
import getOrderSubscriptionFields from './OrderSubscriptionModal.fields'
import translations from '../../../../AccountOrders/AccountOrderDetail/OrderProductItemsList/OrderProductItemsList.constants'
import * as S from '../../../../AccountOrders/AccountOrderDetail/OrderProductItemsList/OrderProductItemsList.styles'
import { IconButton } from '@/components/molecules/IconButton'
import modalTranslations from './OrderSubscriptionModal.constants'

const OrderSubscriptionModal = ({
  isProcessing,
  setIsProcessing,
  standingOrder
}: OrderSubscriptionModalProps) => {
  const { formatMessage, formatNumber } = useIntl()
  const form = useForm()
  const i18n = useCountryData()
  const {
    theme: { font }
  } = useConfig()
  const alertBoxRef = useRef<AlertBox>()
  const subscriptionModalEmitter = useRef(EventsEmitter)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [products, setProducts] = useState([])
  const [removedProducts, setRemovedProducts] = useState([])
  const { paymentInstruments = [], addresses: allAdresses = [] } = useCustomer()
  const { currency } = useBasket()
  const [addresses, setAddresses] = useState(allAdresses)
  const [payment, setPayment] = useState()
  const {
    createList,
    createListItem,
    update,
    updateListItem,
    removeListItem,
    updateOrderWithItems
  } = useStandingOrders()
  const isEdit = !!standingOrder

  useEffect(() => {
    onPaymentMethodChange({
      target: { value: fieldConfig.paymentMethod.options?.[0]?.id }
    })
    if (isEdit) {
      form.setValue('interval', standingOrder.c_orderingInterval)
      form.setValue('startDate', standingOrder.c_orderingNextShippingDate)
      setPaymentMethod(standingOrder.c_orderingPayment.registrationId)
      setFormAddresses(
        standingOrder.c_orderingShippingAddress,
        standingOrder.c_orderingBillingAddress
      )
    }
  }, [isModalOpen])

  useEffect(() => {
    if (
      !subscriptionModalEmitter.current?.has(Events.OPEN_SUBSCRIPTION_MODAL)
    ) {
      subscriptionModalEmitter.current?.subscribe(
        Events.OPEN_SUBSCRIPTION_MODAL,
        (params: any) => {
          if (!isModalOpen) {
            setProducts(params.products)
            setIsModalOpen(true)
            setTimeout(() => {
              params.paymentID && setPaymentMethod(params.paymentID)
              setFormAddresses(params.shippingAddress, params.billingAddress)
            }, 10)
          }
        }
      )
    }

    return () => {
      subscriptionModalEmitter.current?.unsubscribe(
        Events.OPEN_SUBSCRIPTION_MODAL
      )
    }
  }, [isModalOpen, products])

  const setPaymentMethod = (paymentID: string) => {
    const _paymentInstrument = paymentInstruments.find(
      (pI: PaymentInstrument) => pI.c_registrationID === paymentID
    )
    if (_paymentInstrument) {
      form.setValue('paymentMethod', _paymentInstrument.paymentInstrumentId)
      onPaymentMethodChange({
        target: { value: _paymentInstrument.paymentInstrumentId }
      })
    }
  }

  const setFormAddresses = (
    shippingAddress: ICustomerAddress,
    billingAddress: ICustomerAddress
  ) => {
    const _shippingAddress = addresses.find(
      (addr) =>
        addr.address1 == shippingAddress?.address1 &&
        addr.postalCode == shippingAddress?.postalCode &&
        addr.firstName == shippingAddress?.firstName
    )
    const _billingAddress = addresses.find(
      (addr) =>
        addr.address1 == billingAddress?.address1 &&
        addr.postalCode == billingAddress?.postalCode &&
        addr.firstName == billingAddress?.firstName
    )
    _shippingAddress &&
      form.setValue('shippingAddress', _shippingAddress.addressId)
    _billingAddress &&
      form.setValue('billingAddress', _billingAddress.addressId)
  }

  const onPaymentMethodChange = (evt: any) => {
    if (evt.target?.value) {
      const paymentInstrument = paymentInstruments.find(
        (pI) => pI.paymentInstrumentId === evt.target.value
      )
      if (paymentInstrument) {
        setPayment(paymentInstrument)
        switch (paymentInstrument.paymentMethodId) {
          case PAYMENT_METHODS.unzer_direct_debit_secured:
          case PAYMENT_METHODS.unzer_invoice:
            const billingAddress = JSON.parse(
              paymentInstrument.c_billingAddress
            )
            billingAddress.addressId = 'default'
            setAddresses([billingAddress])
            form.setValue('shippingAddress', 'default')
            form.setValue('billingAddress', 'default')
            break
          default:
            setAddresses(allAdresses)
            form.setValue('shippingAddress', allAdresses[0].addressId)
            form.setValue(
              'billingAddress',
              allAdresses.filter(
                (addr) =>
                  !addr?.c_isPackstation &&
                  addr?.countryCode === i18n.currentCountry.code
              )?.[0].addressId || ''
            )
            break
        }
      }
    }
  }

  const fieldConfig = getOrderSubscriptionFields(
    addresses,
    paymentInstruments,
    onPaymentMethodChange
  )

  const fallBackImage = getAssetUrl('static/img/fallback-product-image.png')
  const handleImageNotFound = (el: HTMLImageElement) => {
    if (el.src !== fallBackImage) {
      el.src = fallBackImage
    }
  }

  const modalHeader = (
    <div
      className={`${font.regular} text-12 leading-12 tracking-0 mb-5 uppercase`}
    >
      {formatMessage(
        isEdit ? modalTranslations.titleEdit : modalTranslations.title
      )}
    </div>
  )

  const closeModal = () => {
    setIsModalOpen(false)
  }

  const closeButtonClick = () => {
    closeModal()
  }

  const submitForm = async (data: any, e: any) => {
    setIsProcessing?.(true)

    const shippingAddress = addresses.find(
      (a) => a.addressId === data.shippingAddress
    )
    const billingAddress = addresses.find(
      (a) => a.addressId === data.billingAddress
    )
    const paymentBillingAddr = payment.c_billingAddress
      ? JSON.parse(payment.c_billingAddress)
      : undefined
    const _standingOrder = {
      c_orderingInterval: parseInt(data.interval),
      c_orderingStartDate: new Date(data.startDate).toISOString(),
      c_orderingStatus: isEdit ? standingOrder.c_orderingStatus : 1,
      c_orderingShipping: {
        shippingId: 'dhl'
      },
      c_orderingPayment: {
        registrationId: payment.c_registrationID,
        paymentMethod: payment.paymentMethodId,
        paymentMethodName: Object.keys(PAYMENT_METHODS).find(
          (key) => PAYMENT_METHODS[key] === payment.paymentMethodId
        ),
        ...(paymentBillingAddr && {
          billingAddress: {
            address1: paymentBillingAddr.address1,
            address2: paymentBillingAddr.address2 ?? '',
            city: paymentBillingAddr.city,
            postalCode: paymentBillingAddr.postalCode,
            countryCode: paymentBillingAddr.countryCode,
            title: paymentBillingAddr.title,
            firstName: paymentBillingAddr.firstName,
            lastName: paymentBillingAddr.lastName,
            salutation: paymentBillingAddr.salutation,
            c_isPackstation: paymentBillingAddr?.c_isPackstation
          }
        })
      },
      c_orderingShippingAddress: {
        address1: shippingAddress.address1,
        address2: shippingAddress.address2 ?? '',
        city: shippingAddress.city,
        postalCode: shippingAddress.postalCode,
        countryCode: shippingAddress.countryCode,
        title: shippingAddress.title,
        firstName: shippingAddress.firstName,
        lastName: shippingAddress.lastName,
        salutation: shippingAddress.salutation,
        c_isPackstation: shippingAddress?.c_isPackstation
      },
      c_orderingBillingAddress: {
        address1: billingAddress.address1,
        address2: billingAddress.address2 ?? '',
        city: billingAddress.city,
        postalCode: billingAddress.postalCode,
        countryCode: billingAddress.countryCode,
        title: billingAddress.title,
        firstName: billingAddress.firstName,
        lastName: billingAddress.lastName,
        salutation: billingAddress.salutation,
        c_isPackstation: billingAddress?.c_isPackstation
      }
    }
    if (isEdit) {
      const productsToUpdate = products.filter(
        (p) =>
          standingOrder?.customerProductListItems?.find(
            (_p) => _p?.id === p?.orderItemId
          )?.quantity !== p?.quantity
      )

      updateOrderWithItems(
        {
          id: standingOrder.id,
          ..._standingOrder
        },
        productsToUpdate,
        removedProducts
      )
    } else {
      const _list = await createList(_standingOrder)

      products.forEach((product, i) => {
        setTimeout(async () => {
          const _p = await createListItem(_list.id, {
            id: product.id,
            quantity: product.quantity
          })
        }, 200 * i)
      })
    }
    setIsProcessing?.(false)
    alertBoxRef?.current?.showAlert()
    closeModal()
  }

  const minRequiredTeaBoxes = () =>
    products?.reduce((n, { quantity }) => n + quantity, 0) >=
    MINIMUM_TEA_PACKS_PER_ORDER

  return (
    <>
      <Modal
        isExtended
        style='xl:w-1/2'
        isOpen={isModalOpen}
        onClose={closeModal}
        onCloseButtonClick={closeButtonClick}
        overlay
        closeOnOverlayClick
        blockScroll
        modalHeader={modalHeader}
        modalBody={
          <div className='overflow-auto'>
            <form className='w-full' onSubmit={form.handleSubmit(submitForm)}>
              <div className='pt-2'>
                <Field
                  control={form.control}
                  config={fieldConfig.shippingAddress}
                  error={form.formState.errors?.shippingAddress}
                />
              </div>
              <div className='pt-4'>
                <Field
                  control={form.control}
                  config={fieldConfig.billingAddress}
                  error={form.formState.errors?.billingAddress}
                />
              </div>
              <div className='grid grid-cols-1 gap-4 pt-4 md:grid-cols-2'>
                <Field
                  control={form.control}
                  config={fieldConfig.startDate}
                  error={form.formState.errors?.startDate}
                />
                <Field
                  control={form.control}
                  config={fieldConfig.interval}
                  additionalProps={fieldConfig.interval.additionalProps}
                  error={form.formState.errors?.interval}
                />
              </div>
              <div className='pt-4'>
                <Field
                  control={form.control}
                  config={fieldConfig.paymentMethod}
                  error={form.formState.errors?.paymentMethod}
                />
              </div>
              <div className={`${font.regular} text-14 pt-8`}>
                <div className={`font-bold ${S.TableViewHeadWrapper}`}>
                  <div className={twMerge(S.TableViewArticleName, 'pl-8')}>
                    {formatMessage(translations.tableArticle)}
                  </div>
                  <div
                    className={twMerge(S.TableViewQuantity, 'max-w-[120px]')}
                  >
                    {formatMessage(translations.tableQuantity)}
                  </div>
                  <div className={S.TableViewUnitPrice}>
                    {formatMessage(translations.tableUnitPrice)}
                  </div>
                  <div className={S.TableViewTotal}>
                    {formatMessage(translations.tableTotal)}
                  </div>
                </div>
                {products?.map((product) => (
                  <div
                    key={`so-modal-${product.id}`}
                    className='border-machine2-75 border-machine2-75 flex w-full flex-col border-b py-4 md:flex-row xl:items-center'
                  >
                    <div className={S.ProductLineItemArticleWrapper}>
                      <div className={S.ProductLineDelete}>
                        <IconButton
                          iconName='delete'
                          size='lg'
                          onClick={() => {
                            isEdit &&
                              setRemovedProducts((data) => [
                                ...data,
                                product.orderItemId
                              ])
                            setProducts((data) =>
                              data.filter((p) => p.id !== product.id)
                            )
                          }}
                        />
                      </div>
                      <div className={S.ProductLineItemArticleImageWrapper}>
                        <div className={S.ProductLineItemArticleImageContainer}>
                          {product.c_image && (
                            <img
                              className={S.ProductLineItemArticleImage}
                              src={product.c_image}
                              onError={(ev) =>
                                handleImageNotFound(
                                  ev.target as HTMLImageElement
                                )
                              }
                              alt={product.name}
                            />
                          )}
                          {!product?.c_image && (
                            <img
                              src={fallBackImage}
                              alt={product.name}
                              className={S.ProductLineItemArticleImage}
                            />
                          )}
                        </div>
                      </div>
                      <div className={S.ProductLineItemArticleDetailsWrapper}>
                        <div className={S.ProductLineItemArticleName}>
                          {product.name}
                        </div>
                        <div className={S.ProductLineItemArticleDescription}>
                          {product.c_servings || ''}
                        </div>
                        <div className={S.ProductLineItemArticleNumber}>
                          {formatMessage(translations.articleNumber, {
                            articleNo: product.id
                          })}
                        </div>
                      </div>
                    </div>
                    <div
                      className={twMerge(
                        S.ProductLineItemTableQuantity,
                        'block max-w-[120px] max-md:mt-2'
                      )}
                    >
                      <ProductQuantity
                        productId={product.id}
                        selectedQuantity={product.quantity}
                        limit={0}
                        width={'w-fit'}
                        isSubmit={false}
                        onChange={(quantity) =>
                          setProducts((data) =>
                            data.map((p) =>
                              p.id === product.id
                                ? {
                                    ...p,
                                    quantity,
                                    price: p.pricePerUnit * quantity
                                  }
                                : p
                            )
                          )
                        }
                      />
                    </div>
                    <div
                      className={twMerge(
                        S.ProductLineItemTableUnitPrice,
                        'block text-left font-bold max-md:mt-2 md:text-right'
                      )}
                    >
                      {formatNumber(product.pricePerUnit, {
                        style: 'currency',
                        currency,
                        currencyDisplay: 'code'
                      })}
                    </div>
                    <div
                      className={`${S.ProductLineItemTableTotal} font-bold max-md:mt-2`}
                    >
                      <div
                        className={
                          product.priceAfterItemDiscount &&
                          product?.c_priceDiscounted &&
                          product.priceAfterItemDiscount !== product.price
                            ? S.ProductLineItemTableTotalBasePrice
                            : ''
                        }
                      >
                        {formatNumber(product.price, {
                          style: 'currency',
                          currency,
                          currencyDisplay: 'code'
                        })}
                      </div>
                      {product.priceAfterItemDiscount &&
                        product?.c_priceDiscounted &&
                        product.priceAfterItemDiscount !== product.price && (
                          <div
                            className={S.ProductLineItemTableTotalPromoPrice}
                          >
                            {formatNumber(
                              product?.c_priceDiscounted * product?.quantity,
                              {
                                style: 'currency',
                                currency,
                                currencyDisplay: 'code'
                              }
                            )}
                          </div>
                        )}
                    </div>

                    <div
                      className={twMerge(
                        S.ProductLineItemDetailsWrapper,
                        'flex-col md:flex-row'
                      )}
                    >
                      <div className={S.ProductLineItemQuantityStatusWrapper}>
                        <div className={S.ProductLineItemQuantityWrapper}>
                          {product.quantity} x{' '}
                          {formatNumber(product.pricePerUnit, {
                            style: 'currency',
                            currency,
                            currencyDisplay: 'code'
                          })}
                        </div>
                      </div>
                      <div
                        className={twMerge(
                          S.ProductLineItemPriceInfoWrapper,
                          'text-left max-md:mt-1 md:text-right'
                        )}
                      >
                        <div
                          className={
                            product.priceAfterItemDiscount &&
                            product.priceAfterItemDiscount !== product.price
                              ? S.ProductLineItemTableTotalBasePrice
                              : ''
                          }
                        >
                          {formatNumber(product.price, {
                            style: 'currency',
                            currency,
                            currencyDisplay: 'code'
                          })}
                        </div>
                        {product.priceAfterItemDiscount &&
                          product.priceAfterItemDiscount !== product.price && (
                            <div
                              className={S.ProductLineItemTableTotalPromoPrice}
                            >
                              {formatNumber(product.c_priceDiscounted, {
                                style: 'currency',
                                currency,
                                currencyDisplay: 'code'
                              })}
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {!minRequiredTeaBoxes() && (
                <span className={`text-errorRed `}>
                  {formatMessage(modalTranslations.minTeaBoxes)}
                </span>
              )}
              <div className='flex flex-col px-1 pb-2 pt-8 md:flex-row md:justify-end'>
                <Button
                  type={'secondary'}
                  className='w-full md:w-auto'
                  variant='outline'
                  onClick={closeButtonClick}
                >
                  {formatMessage(modalTranslations.cancel)}
                </Button>
                <Button
                  type='secondary'
                  className='mt-4 w-full md:ml-4 md:mt-0 md:w-auto'
                  isDisabled={!minRequiredTeaBoxes()}
                  isSubmit
                >
                  {formatMessage(
                    isEdit ? modalTranslations.edit : modalTranslations.create
                  )}
                </Button>
              </div>
            </form>
          </div>
        }
      />
      <AlertBox
        ref={alertBoxRef}
        type='success'
        title={formatMessage(
          isEdit ? modalTranslations.successEdit : modalTranslations.successEdit
        )}
        autoCloseTime={2000}
        position='bottom'
        showBackdrop={false}
      />
    </>
  )
}

export default OrderSubscriptionModal
